import React, { useState, useRef, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Calendar, Clock } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface DateTimeRangePickerProps {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (value: [Dayjs, Dayjs] | null) => void;
  format?: string;
  className?: string;
  disabled?: boolean;
}

export const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  value,
  onChange,
  format = "MMM DD, HH:mm",
  className,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");

  // Sync internal state with value prop
  useEffect(() => {
    if (value?.[0]) {
      setStartDate(value[0].format("YYYY-MM-DD"));
      setStartTime(value[0].format("HH:mm"));
    }
    if (value?.[1]) {
      setEndDate(value[1].format("YYYY-MM-DD"));
      setEndTime(value[1].format("HH:mm"));
    }
  }, [value]);

  const formatDisplay = (date: Dayjs | null | undefined): string => {
    if (!date) return "";
    return date.format(format);
  };

  const handleApply = () => {
    if (startDate && startTime && endDate && endTime) {
      const start = dayjs(`${startDate} ${startTime}`);
      const end = dayjs(`${endDate} ${endTime}`);
      onChange?.([start, end]);
    }
    setOpen(false);
  };

  const displayValue = value?.[0] && value?.[1]
    ? `${formatDisplay(value[0])} - ${formatDisplay(value[1])}`
    : "Select date range";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal h-9 px-3",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          <span className="text-sm">{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          {/* Start DateTime */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Start</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <div className="relative">
                <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="flex h-9 w-[100px] rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </div>
          </div>

          {/* End DateTime */}
          <div className="space-y-2">
            <label className="text-sm font-medium">End</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <div className="relative">
                <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="flex h-9 w-[100px] rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
